import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/useSEO';
import { getImageUrl } from '../utils/api';
import { getPersonaLabel } from '../utils/expertRef';
import { API_BASE_URL } from '../config';

const LAGOM_GREEN = '#7cb342';
const LAGOM_GREEN_DARK = '#689f38';

const Page = styled.main`
  max-width: 1180px;
  margin: 0 auto;
  padding: 68px 24px 88px;
`;

const PageHeader = styled.header`
  text-align: center;
  margin-bottom: 48px;

  h1 {
    font-family: var(--font-heading);
    font-size: clamp(2rem, 4vw, 2.5rem);
    color: #222;
    margin: 0 0 14px;
    letter-spacing: 0.02em;
  }

  p {
    color: #666;
    font-size: 18px;
    line-height: 1.65;
    max-width: 560px;
    margin: 0 auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 350px));
  gap: 28px;
  justify-content: center;
`;

const Card = styled(Link)`
  text-decoration: none;
  color: inherit;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #eee;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;

  &:hover {
    border-color: ${LAGOM_GREEN};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-3px);
  }
`;

const Photo = styled.div`
  position: relative;
  height: 350px;
  overflow: hidden;
  background-color: #eee;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: center 68%;
    transform: scale(1.16);
    transition: transform .35s ease;
  }

  ${Card}:hover & img {
    transform: scale(1.2);
  }

  @media (prefers-reduced-motion: reduce) {
    img { transition: none; }
  }
`;

const Body = styled.div`
  padding: 20px 22px 24px;
`;

const ExpertBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${LAGOM_GREEN_DARK};
  background: #f1f8e9;
  padding: 5px 12px;
  border-radius: 6px;
  margin-bottom: 10px;
`;

const Name = styled.h2`
  font-family: var(--font-heading);
  margin: 0 0 10px;
  font-size: 1.35rem;
  color: #222;
  line-height: 1.2;
`;

const Tagline = styled.p`
  margin: 0;
  color: #666;
  font-size: 16px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FooterCta = styled.div`
  text-align: center;
  margin-top: 56px;
`;

const BecomeLink = styled(Link)`
  display: inline-block;
  padding: 14px 32px;
  background: #ff6b35;
  color: #fff;
  border-radius: 999px;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover { opacity: 0.92; color: #fff; }
`;

const ExpertsList = () => {
  const [experts, setExperts] = useState([]);

  usePageSEO({
    title: 'Эксперты — Lagom Life',
    description: 'Эксперты Lagom Life: научный подход, программы питания и тренировок, рекомендации продуктов.',
    jsonLd: experts.length ? {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: experts.map((e, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${window.location.origin}/expert/${e.slug}`,
        name: e.displayName
      }))
    } : null
  });

  useEffect(() => {
    fetch(`${API_BASE_URL}/experts`)
      .then(r => r.json())
      .then(d => setExperts(d.data || []))
      .catch(() => setExperts([]));
  }, []);

  return (
    <Page>
      <PageHeader>
        <h1>Эксперты Lagom Life</h1>
        <p>Специалисты, которые задают вектор развития: научный подход, практика и гармония здорового образа жизни</p>
      </PageHeader>
      <Grid>
        {experts.map(e => (
          <Card key={e.slug} to={`/expert/${e.slug}`}>
            <Photo>
              <img
                src={e.photoUrl ? getImageUrl(e.photoUrl) : '/nofoto.png'}
                alt={e.displayName}
                loading="lazy"
              />
            </Photo>
            <Body>
              <ExpertBadge>{getPersonaLabel(e.personaType) || 'Эксперт'}</ExpertBadge>
              <Name>{e.displayName}</Name>
              {e.tagline && <Tagline>{e.tagline}</Tagline>}
            </Body>
          </Card>
        ))}
      </Grid>
      <FooterCta>
        <BecomeLink to="/become-expert">Стать экспертом</BecomeLink>
      </FooterCta>
    </Page>
  );
};

export default ExpertsList;
